import { Component, inject, Input, OnChanges } from '@angular/core';
import { ShowPerformance } from '../../../models/showperformance.model';
import { Showreport } from '../../showreport/showreport.model';
import { AlertController } from '@ionic/angular';
import { Show } from '../../../models/show.model';

export type TechnicalItemsDisplayMode = 'compact' | 'detailed' | 'icons-only';

@Component({
    selector: 'app-showreport-technical-items-preview-list',
    templateUrl: './showreport-technical-items-preview-list.component.html',
    styleUrls: ['./showreport-technical-items-preview-list.component.scss'],
    standalone: false
})
export class ShowreportTechnicalItemsPreviewListComponent implements OnChanges {

    /**
     * Performance input - used on dashboard/performance cards.
     * Either performance or showreport must be provided.
     */
    @Input() public performance: ShowPerformance;

    /**
     * Direct showreport input - used on showreport detail page.
     * Either performance or showreport must be provided.
     */
    @Input() public showreport: Showreport;

    /**
     * Direct show input - used when showreport is provided directly.
     */
    @Input() public show: Show;

    /**
     * Display mode: 'compact' shows a table, 'detailed' shows a vertical list.
     */
    @Input() public displayMode: TechnicalItemsDisplayMode = 'compact';

    /**
     * Whether clicking on items with messages opens an alert.
     */
    @Input() public clickable: boolean = true;

    private readonly alertController: AlertController = inject(AlertController);

    /**
     * Cached check rows the template iterates over. Recomputed via
     * `ngOnChanges` whenever the inputs change. Was previously exposed as
     * a getter and read directly in `@for`, which built a new array on
     * every change-detection cycle — with `track check` (reference
     * equality) Angular tore down and rebuilt every row in a tight loop
     * and produced an avalanche of NG0100 errors.
     */
    public reportChecks: { name: string; value: boolean; message: string | null }[] = [];

    public ngOnChanges(): void {
        this.reportChecks = this.computeReportChecks();
    }

    /**
     * Get the showreport from either direct input or via performance.
     */
    private getShowreport(): Showreport | null {
        return this.showreport || this.performance?.showreport || null;
    }

    /**
     * Get the show from either direct input or via performance.
     */
    private getShow(): Show | null {
        return this.show || this.performance?.show || null;
    }

    /**
     * Check if this showreport uses dynamic template fields.
     * Legacy shows always use the hardcoded format.
     */
    public isDynamicShowreport(): boolean {
        const show = this.getShow();
        if (show?.useLegacyShowreport) {
            return false;
        }
        const showreport = this.getShowreport();
        return showreport?.fieldValues?.length > 0;
    }

    private computeReportChecks(): {
        name: string;
        value: boolean;
        message: string | null;
    }[] {
        const showreport = this.getShowreport();
        if (!showreport) {
            return [];
        }

        // For dynamic showreports, extract checkbox fields from fieldValues
        if (this.isDynamicShowreport()) {
            return this.getDynamicCheckboxFields();
        }

        // Legacy showreports use hardcoded fields
        const checks = [
            'lightCheck',
            'followMeCheck',
            'audioCheck',
            'flamesCheck',
            'revolveCheck',
            'hoistsCheck',
            'sparkularsCheck',
            'waterscreenCheck',
            'trapdoorsCheck'
        ];

        const checkNames = [
            'Licht',
            'Follow Me',
            'Geluid',
            'Vuur',
            'Draaischijf',
            'Takels',
            'Sparkulars',
            'Waterscherm',
            'Luiken'
        ];

        return checks.map((check, index) => {
            const value = !!showreport[check];
            const message = showreport[`${ check }Message`] || null;
            return {
                name: checkNames[index],
                value,
                message
            };
        });
    }

    /**
     * Extract checkbox and technical item fields from dynamic showreport field values.
     */
    private getDynamicCheckboxFields(): { name: string; value: boolean; message: string | null }[] {
        const showreport = this.getShowreport();
        const fieldValues = showreport?.fieldValues || [];

        return fieldValues
            .filter(field => field.fieldType === 'CHECKBOX' || field.fieldType === 'TECHNICAL_ITEM')
            .sort((a, b) => (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0))
            .map(field => {
                if (field.fieldType === 'TECHNICAL_ITEM') {
                    // TECHNICAL_ITEM stores value as JSON: {"check": boolean, "message": string}
                    try {
                        const parsed = typeof field.value === 'string'
                            ? JSON.parse(field.value)
                            : field.value;
                        return {
                            name: field.label,
                            value: parsed?.check ?? false,
                            message: parsed?.message || null
                        };
                    } catch {
                        return {
                            name: field.label,
                            value: false,
                            message: null
                        };
                    }
                }
                // CHECKBOX type
                return {
                    name: field.label,
                    value: field.value === 'true',
                    message: null
                };
            });
    }

    public async openCheckAlert(check: { name: string; value: boolean; message: string }): Promise<void> {
        if (!this.clickable || !check.message) {
            return;
        }

        const alert = await this.alertController.create({
            header: check.name,
            message: check.message,
            buttons: ['Sluiten']
        });

        await alert.present();
    }
}
